import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';

const INDIA_COUNTRY = 'India';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

const normalizeStateName = (stateName = '') => {
  const normalized = stateName.trim().toLowerCase();
  return INDIAN_STATES.find((state) => state.toLowerCase() === normalized) || stateName;
};

const Step2Address = ({ formData, formErrors, handleChange, isSubmitting, isOtpStep }) => {
  const [isPincodeLookupLoading, setIsPincodeLookupLoading] = useState(false);
  const [currentCityOptions, setCurrentCityOptions] = useState([]);
  const [permanentCityOptions, setPermanentCityOptions] = useState([]);
  const [districtSuggestions, setDistrictSuggestions] = useState([]);

  const setFieldValue = (name, value) => {
    handleChange({ target: { name, value, type: 'text' } });
  };

  useEffect(() => {
    if (formData.currentCountry !== INDIA_COUNTRY) {
      setFieldValue('currentCountry', INDIA_COUNTRY);
    }
    if (!formData.addressSameAsCurrent && formData.permanentCountry !== INDIA_COUNTRY) {
      setFieldValue('permanentCountry', INDIA_COUNTRY);
    }
  }, [formData.currentCountry, formData.permanentCountry, formData.addressSameAsCurrent]);

  const fetchSuggestions = async (query, setSuggestions) => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&countrycodes=in&limit=8&q=${encodeURIComponent(query)}`
      );
      const data = await response.json().catch(() => []);
      if (!response.ok || !Array.isArray(data)) {
        setSuggestions([]);
        return;
      }
      setSuggestions(data);
    } catch {
      setSuggestions([]);
    }
  };

  const applyPincodeLookup = async (fieldPrefix, numericPincode) => {
    const cityField = `${fieldPrefix}CityTown`;
    const districtField = `${fieldPrefix}District`;
    const stateField = `${fieldPrefix}State`;
    const countryField = `${fieldPrefix}Country`;

    const response = await fetch(`https://api.postalpincode.in/pincode/${numericPincode}`);
    const data = await response.json().catch(() => []);
    const firstResult = Array.isArray(data) ? data[0] : null;
    const postOffice = firstResult?.PostOffice?.[0];
    const allPostOffices = Array.isArray(firstResult?.PostOffice) ? firstResult.PostOffice : [];

    if (firstResult?.Status !== 'Success' || !postOffice) {
      if (fieldPrefix === 'current') {
        setCurrentCityOptions([]);
      } else {
        setPermanentCityOptions([]);
      }
      setFieldValue(cityField, '');
      setFieldValue(districtField, '');
      setFieldValue(stateField, '');
      setFieldValue(countryField, INDIA_COUNTRY);
      await Swal.fire({
        icon: 'error',
        text: 'Pincode not found. Please enter a valid Indian pincode.',
        confirmButtonColor: '#c7374e',
      });
      return;
    }

    const uniqueCityOptions = Array.from(
      new Set(
        allPostOffices
          .map((item) => item?.Name)
          .filter(Boolean)
      )
    );

    if (fieldPrefix === 'current') {
      setCurrentCityOptions(uniqueCityOptions);
    } else {
      setPermanentCityOptions(uniqueCityOptions);
    }

    const selectedCity = uniqueCityOptions.includes(formData[cityField])
      ? formData[cityField]
      : (uniqueCityOptions[0] || '');

    setFieldValue(cityField, selectedCity);
    setFieldValue(districtField, postOffice.District || '');
    setFieldValue(stateField, normalizeStateName(postOffice.State || ''));
    setFieldValue(countryField, INDIA_COUNTRY);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchSuggestions(formData.currentDistrict, setDistrictSuggestions);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [formData.currentDistrict]);

  const districtSuggestionOptions = useMemo(() => {
    const unique = new Set();
    districtSuggestions.forEach((item) => {
      const district = item?.address?.state_district || item?.address?.county || item?.address?.district;
      if (district) unique.add(district);
    });
    return Array.from(unique);
  }, [districtSuggestions]);

  const handlePincodeChange = (name) => async (event) => {
    const numericPincode = event.target.value.replace(/\D/g, '').slice(0, 6);
    setFieldValue(name, numericPincode);

    if (numericPincode.length !== 6) {
      if (name === 'currentPincode') {
        setCurrentCityOptions([]);
      } else {
        setPermanentCityOptions([]);
      }
      const prefix = name === 'currentPincode' ? 'current' : 'permanent';
      setFieldValue(`${prefix}CityTown`, '');
      setFieldValue(`${prefix}District`, '');
      setFieldValue(`${prefix}State`, '');
      setFieldValue(`${prefix}Country`, INDIA_COUNTRY);
      return;
    }

    setIsPincodeLookupLoading(true);
    try {
      const prefix = name === 'currentPincode' ? 'current' : 'permanent';
      await applyPincodeLookup(prefix, numericPincode);
    } catch {
      if (name === 'currentPincode') {
        setCurrentCityOptions([]);
      } else {
        setPermanentCityOptions([]);
      }
      const prefix = name === 'currentPincode' ? 'current' : 'permanent';
      setFieldValue(`${prefix}CityTown`, '');
      setFieldValue(`${prefix}District`, '');
      setFieldValue(`${prefix}State`, '');
      setFieldValue(`${prefix}Country`, INDIA_COUNTRY);
      await Swal.fire({
        icon: 'error',
        text: 'Unable to fetch address from pincode right now.',
        confirmButtonColor: '#c7374e',
      });
    } finally {
      setIsPincodeLookupLoading(false);
    }
  };

  return (
    <>

      <div className="address-section">
        <h3>Current Address</h3>
        
        <div className="application-field full-width">
          <label htmlFor="currentFullAddress">Full Address <span className="required-star">*</span></label>
          <textarea
            id="currentFullAddress"
            name="currentFullAddress"
            value={formData.currentFullAddress}
            onChange={handleChange}
            placeholder="Enter your current full address"
            rows="2"
            disabled={isSubmitting}
          />
          {formErrors.currentFullAddress && <p className="application-error">{formErrors.currentFullAddress}</p>}
        </div>

        <div className="application-field">
          <label htmlFor="currentPincode">Pincode <span className="required-star">*</span></label>
          <input
            id="currentPincode"
            name="currentPincode"
            value={formData.currentPincode}
            onChange={handlePincodeChange('currentPincode')}
            placeholder="6 digit pincode"
            maxLength="6"
            inputMode="numeric"
            pattern="[0-9]{6}"
            disabled={isSubmitting || isPincodeLookupLoading}
          />
          {formErrors.currentPincode && <p className="application-error">{formErrors.currentPincode}</p>}
        </div>

        <div className="application-field">
          <label htmlFor="currentCityTown">City/Town <span className="required-star">*</span></label>
          <select
            id="currentCityTown"
            name="currentCityTown"
            value={formData.currentCityTown || ''}
            onChange={handleChange}
            disabled={isSubmitting}
          >
            <option value="">
              {currentCityOptions.length === 0 ? 'Enter pincode first' : 'Select city/town'}
            </option>
            {currentCityOptions.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
          {formErrors.currentCityTown && <p className="application-error">{formErrors.currentCityTown}</p>}
        </div>
        
        <div className="application-field">
          <label htmlFor="currentDistrict">District <span className="required-star">*</span></label>
          <input
            id="currentDistrict"
            name="currentDistrict"
            value={formData.currentDistrict}
            onChange={handleChange}
            placeholder="Enter district"
            disabled={isSubmitting}
          />
          {formErrors.currentDistrict && <p className="application-error">{formErrors.currentDistrict}</p>}
        </div>

        <div className="application-field">
          <label htmlFor="currentState">State <span className="required-star">*</span></label>
          <select
            id="currentState"
            name="currentState"
            value={formData.currentState}
            onChange={handleChange}
            disabled={isSubmitting}
          >
            <option value="">Select state</option>
            {INDIAN_STATES.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
          {formErrors.currentState && <p className="application-error">{formErrors.currentState}</p>}
        </div>

        <div className="application-field">
          <label htmlFor="currentCountry">Country <span className="required-star">*</span></label>
          <select
            id="currentCountry"
            name="currentCountry"
            value={formData.currentCountry}
            onChange={handleChange}
            disabled
          >
            <option value={INDIA_COUNTRY}>{INDIA_COUNTRY}</option>
          </select>
          {formErrors.currentCountry && <p className="application-error">{formErrors.currentCountry}</p>}
        </div>
      </div>

      <div className="checkbox-field">
        <label>
          <input
            type="checkbox"
            name="addressSameAsCurrent"
            checked={formData.addressSameAsCurrent || false}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          Permanent address is same as current address
        </label>
      </div>

      {!formData.addressSameAsCurrent && (
        <div className="address-section">
          <h3>Permanent Address</h3>
          
          <div className="application-field full-width">
            <label htmlFor="permanentFullAddress">Full Address <span className="required-star">*</span></label>
            <textarea
              id="permanentFullAddress"
              name="permanentFullAddress"
              value={formData.permanentFullAddress}
              onChange={handleChange}
              placeholder="Enter your full permanent address"
              rows="2"
              disabled={isSubmitting}
            />
            {formErrors.permanentFullAddress && <p className="application-error">{formErrors.permanentFullAddress}</p>}
          </div>

          <div className="application-field">
            <label htmlFor="permanentPincode">Pincode <span className="required-star">*</span></label>
            <input
              id="permanentPincode"
              name="permanentPincode"
              value={formData.permanentPincode}
              onChange={handlePincodeChange('permanentPincode')}
              placeholder="6 digit pincode"
              maxLength="6"
              inputMode="numeric"
              pattern="[0-9]{6}"
              disabled={isSubmitting}
            />
            {formErrors.permanentPincode && <p className="application-error">{formErrors.permanentPincode}</p>}
          </div>

          <div className="application-field">
            <label htmlFor="permanentCityTown">City/Town <span className="required-star">*</span></label>
            <select
              id="permanentCityTown"
              name="permanentCityTown"
              value={formData.permanentCityTown}
              onChange={handleChange}
              disabled={isSubmitting || permanentCityOptions.length === 0}
            >
              <option value="">
                {permanentCityOptions.length === 0 ? 'Enter pincode first' : 'Select city/town'}
              </option>
              {permanentCityOptions.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            {formErrors.permanentCityTown && <p className="application-error">{formErrors.permanentCityTown}</p>}
          </div>

          <div className="application-field">
            <label htmlFor="permanentDistrict">District <span className="required-star">*</span></label>
            <input
              id="permanentDistrict"
              name="permanentDistrict"
              value={formData.permanentDistrict}
              onChange={handleChange}
              placeholder="Enter district"
              disabled={isSubmitting}
            />
            {formErrors.permanentDistrict && <p className="application-error">{formErrors.permanentDistrict}</p>}
          </div>

          <div className="application-field">
            <label htmlFor="permanentState">State <span className="required-star">*</span></label>
            <select
              id="permanentState"
              name="permanentState"
              value={formData.permanentState}
              onChange={handleChange}
              disabled={isSubmitting}
            >
              <option value="">Select state</option>
              {INDIAN_STATES.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {formErrors.permanentState && <p className="application-error">{formErrors.permanentState}</p>}
          </div>

          <div className="application-field">
            <label htmlFor="permanentCountry">Country <span className="required-star">*</span></label>
            <select
              id="permanentCountry"
              name="permanentCountry"
              value={formData.permanentCountry}
              onChange={handleChange}
              disabled
            >
              <option value={INDIA_COUNTRY}>{INDIA_COUNTRY}</option>
            </select>
            {formErrors.permanentCountry && <p className="application-error">{formErrors.permanentCountry}</p>}
          </div>
        </div>
      )}

      {isOtpStep && (
        <div className="application-field otp-field full-width">
          <label htmlFor="otp">OTP Verification <span className="required-star">*</span></label>
          <input
            id="otp"
            name="otp"
            value={formData.otp}
            onChange={handleChange}
            placeholder="Enter OTP sent to your email"
            disabled={isSubmitting}
          />
          {formErrors.otp && <p className="application-error">{formErrors.otp}</p>}
        </div>
      )}
    </>
  );
};

export default Step2Address;